import React from 'react';
import { observer } from 'mobx-react';
import { action, makeObservable, observable } from 'mobx';
import {
    Alert, Button, Card, Form, Input, Popconfirm, Space, Spin, Table, Tag, Typography, notification,
} from 'antd';
import { computed } from 'mobx';
import { NotificationsProxy } from '../services/catalogServiceAPI/notificationsProxy';
import { nonBmpError } from '../utils/textValidation';
import { parseList } from '../utils/parseList';
import { ASSETS_BASE_URL, validateImageUrl } from '../utils/validateImageUrl';

const { TextArea } = Input;

/**
 * Single-page composer + history. v1 supports per-user sends only — paste
 * UIDs and/or emails (one per line). Emails are resolved server-side via
 * Firebase Admin; unresolved ones come back in the result so the admin can
 * fix typos. CTA is limited to a `navigate` href; system-only CTAs like
 * `open_reward_reveal` aren't author-able from here.
 */
@observer
export class Notifications extends React.Component {
    proxy = new NotificationsProxy();

    @observable isLoading = false;
    @observable isSending = false;
    @observable history = [];
    @observable form = {
        targetUids: '',
        targetEmails: '',
        title: '',
        body: '',
        imageUrl: '',
        iconToken: '',
        ctaHref: '',
        ctaText: '',
    };
    /** Last verify-step result; null until the admin clicks "Verify". */
    @observable resolvePreview = null;
    /** Last send result; null until a successful send. */
    @observable lastResult = null;

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    @computed get titleError() { return nonBmpError(this.form.title); }
    @computed get bodyError()  { return nonBmpError(this.form.body); }

    @computed get imageUrlError() {
        return validateImageUrl(this.form.imageUrl);
    }

    @action.bound setLoading = (v) => this.isLoading = v;
    @action.bound setSending = (v) => this.isSending = v;
    @action.bound setHistory = (v) => this.history = v;
    @action.bound setField = (k, v) => { this.form[k] = v; };
    @action.bound setResolvePreview = (v) => { this.resolvePreview = v; };
    @action.bound setLastResult = (v) => { this.lastResult = v; };

    componentDidMount() {
        this.fetchHistory();
    }

    async fetchHistory() {
        this.setLoading(true);
        try {
            const rows = await this.proxy.getHistory();
            this.setHistory(rows);
        } catch (e) {
            notification.error({ message: 'Failed to load history', description: e.message });
        } finally {
            this.setLoading(false);
        }
    }

    buildInput = () => ({
        targetUids: parseList(this.form.targetUids),
        targetEmails: parseList(this.form.targetEmails),
        title: this.form.title.trim(),
        body: this.form.body.trim() || null,
        imageUrl: this.form.imageUrl.trim() || null,
        iconToken: this.form.iconToken.trim() || null,
        ctaHref: this.form.ctaHref.trim() || null,
        ctaText: this.form.ctaText.trim() || null,
    });

    /**
     * Single source of truth for "is this composer state valid?". `requireTitle`
     * is false for the Verify path (admin can verify recipients before deciding
     * on a title) and true for Send. Returns `{ message, description }` for the
     * first failure, or null when valid.
     */
    validate(input, { requireTitle }) {
        if (input.targetUids.length === 0 && input.targetEmails.length === 0) {
            return { message: 'No recipients', description: 'Add at least one UID or email.' };
        }
        if (requireTitle && !input.title) {
            return { message: 'Title is required' };
        }
        if (this.imageUrlError) {
            return { message: 'Invalid image URL', description: this.imageUrlError };
        }
        const charErr = this.titleError || this.bodyError;
        if (charErr) return { message: 'Invalid characters', description: charErr };
        return null;
    }

    verify = async () => {
        const input = this.buildInput();
        const err = this.validate(input, { requireTitle: false });
        if (err) { notification.warning(err); return; }
        try {
            const result = await this.proxy.resolveTargets({
                targetUids: input.targetUids,
                targetEmails: input.targetEmails,
            });
            this.setResolvePreview(result);
        } catch (e) {
            notification.error({ message: 'Failed to resolve targets', description: e.message });
        }
    };

    send = async () => {
        const input = this.buildInput();
        const err = this.validate(input, { requireTitle: true });
        if (err) { notification.warning(err); return; }
        this.setSending(true);
        try {
            const result = await this.proxy.send(input);
            this.setLastResult(result);
            if (result.unresolvedEmails && result.unresolvedEmails.length > 0) {
                notification.warning({
                    message: `Sent to ${result.delivered} / ${result.totalRecipients}`,
                    description: `Couldn't resolve: ${result.unresolvedEmails.join(', ')}`,
                });
            } else {
                notification.success({
                    message: `Sent to ${result.delivered} ${result.delivered === 1 ? 'user' : 'users'}`,
                });
            }
            await this.fetchHistory();
        } catch (e) {
            notification.error({ message: 'Failed to send', description: e.message });
        } finally {
            this.setSending(false);
        }
    };

    historyColumns = [
        {
            title: 'Sent',
            dataIndex: 'createdAt',
            width: 170,
            render: (v) => new Date(v).toLocaleString(),
        },
        {
            title: 'Title',
            dataIndex: 'title',
        },
        {
            title: 'Body',
            dataIndex: 'body',
            render: (v) => v || <Typography.Text type="secondary">—</Typography.Text>,
        },
        {
            title: 'CTA',
            dataIndex: 'cta',
            width: 220,
            render: (cta) => cta?.type === 'navigate'
                ? <a href={cta.href} target="_blank" rel="noreferrer">{cta.href}</a>
                : <Typography.Text type="secondary">—</Typography.Text>,
        },
        {
            title: 'State',
            width: 160,
            render: (_, row) => (
                <Space>
                    {row.readAt ? <Tag color="blue">read</Tag> : <Tag>unread</Tag>}
                    {row.dismissedAt && <Tag color="gray">dismissed</Tag>}
                    {row.resolvedAt && <Tag color="green">resolved</Tag>}
                </Space>
            ),
        },
    ];

    render() {
        const targetCount = this.resolvePreview?.uids?.length ?? null;
        const unresolvedCount = this.resolvePreview?.unresolvedEmails?.length ?? 0;

        return (
            <Space direction="vertical" size={16} style={{ width: '100%', padding: '24px 32px' }}>
                <Typography.Title level={3} style={{ margin: 0 }}>Send notification</Typography.Title>

                <Card>
                    <Form layout="vertical">
                        <Form.Item label="Target UIDs" help="One per line, comma- or space-separated.">
                            <TextArea
                                rows={3}
                                value={this.form.targetUids}
                                onChange={e => this.setField('targetUids', e.target.value)}
                                placeholder="abc123uid&#10;def456uid"
                            />
                        </Form.Item>
                        <Form.Item label="Target emails" help="Resolved server-side. Unmatched ones are reported back.">
                            <TextArea
                                rows={3}
                                value={this.form.targetEmails}
                                onChange={e => this.setField('targetEmails', e.target.value)}
                                placeholder="alice@example.com&#10;bob@example.com"
                            />
                        </Form.Item>
                        <Form.Item
                            label="Title"
                            required
                            validateStatus={this.titleError ? 'error' : ''}
                            help={this.titleError || undefined}
                        >
                            <Input
                                value={this.form.title}
                                onChange={e => this.setField('title', e.target.value)}
                                maxLength={120}
                                showCount
                                status={this.titleError ? 'error' : ''}
                            />
                        </Form.Item>
                        <Form.Item
                            label="Body"
                            validateStatus={this.bodyError ? 'error' : ''}
                            help={this.bodyError || undefined}
                        >
                            <TextArea
                                rows={2}
                                value={this.form.body}
                                onChange={e => this.setField('body', e.target.value)}
                                maxLength={400}
                                showCount
                                status={this.bodyError ? 'error' : ''}
                            />
                        </Form.Item>
                        <Form.Item
                            label="Icon (optional)"
                            help="Name of an icon from common/icons (case-insensitive). Examples: Bell, Gift, Trophy, Crown, Flame, Heart, Info, Warning, Coin. Falls back to a default if the name doesn't match."
                        >
                            <Input
                                value={this.form.iconToken}
                                onChange={e => this.setField('iconToken', e.target.value)}
                                placeholder="e.g. Crown"
                                style={{ width: 220 }}
                            />
                        </Form.Item>
                        <Form.Item
                            label="Image URL (optional)"
                            help={this.imageUrlError
                                ? this.imageUrlError
                                : `Must be hosted under ${ASSETS_BASE_URL}. The client uses next/image which only renders images from there.`
                            }
                            validateStatus={this.imageUrlError ? 'error' : ''}
                        >
                            <Input
                                value={this.form.imageUrl}
                                onChange={e => this.setField('imageUrl', e.target.value)}
                                placeholder={`${ASSETS_BASE_URL}/…`}
                                status={this.imageUrlError ? 'error' : ''}
                            />
                        </Form.Item>
                        <Form.Item label="CTA link (optional)" help="If set, a button on the notification navigates here.">
                            <Input
                                value={this.form.ctaHref}
                                onChange={e => this.setField('ctaHref', e.target.value)}
                                placeholder="/challenges or https://…"
                            />
                        </Form.Item>
                        <Form.Item label="CTA button text (optional)" help='Defaults to "Open" when blank.'>
                            <Input
                                value={this.form.ctaText}
                                onChange={e => this.setField('ctaText', e.target.value)}
                                placeholder="Open"
                                maxLength={30}
                                style={{ width: 220 }}
                            />
                        </Form.Item>

                        <Space>
                            <Button onClick={this.verify}>Verify recipients</Button>
                            <Popconfirm
                                title={
                                    targetCount !== null
                                        ? `Send to ${targetCount} ${targetCount === 1 ? 'user' : 'users'}?`
                                        : 'Send notification?'
                                }
                                onConfirm={this.send}
                                okText="Send"
                            >
                                <Button type="primary" loading={this.isSending}>Send</Button>
                            </Popconfirm>
                        </Space>

                        {this.resolvePreview && (
                            <Alert
                                style={{ marginTop: 16 }}
                                type={unresolvedCount > 0 ? 'warning' : 'info'}
                                showIcon
                                message={`${targetCount} resolved recipient${targetCount === 1 ? '' : 's'}`}
                                description={
                                    <Space direction="vertical" size={6} style={{ width: '100%' }}>
                                        {targetCount > 0 && (
                                            <Typography.Paragraph
                                                copyable={{ text: this.resolvePreview.uids.join('\n'), tooltips: ['Copy all UIDs', 'Copied'] }}
                                                style={{ margin: 0, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
                                            >
                                                {this.resolvePreview.uids.join('\n')}
                                            </Typography.Paragraph>
                                        )}
                                        {unresolvedCount > 0 && (
                                            <Typography.Text type="danger">
                                                {unresolvedCount} email{unresolvedCount === 1 ? '' : 's'} couldn&apos;t be matched: {this.resolvePreview.unresolvedEmails.join(', ')}
                                            </Typography.Text>
                                        )}
                                    </Space>
                                }
                            />
                        )}
                    </Form>
                </Card>

                <Typography.Title level={4} style={{ margin: '8px 0 0' }}>
                    History {this.history.length > 0 && <Tag>{this.history.length}</Tag>}
                </Typography.Title>
                {this.isLoading
                    ? <Spin />
                    : (
                        <Table
                            rowKey="id"
                            dataSource={this.history.slice()}
                            columns={this.historyColumns}
                            pagination={{ pageSize: 20 }}
                            size="middle"
                        />
                    )
                }
            </Space>
        );
    }
}
